import React, { useState } from 'react';
import { Film, ClipboardList, FileText, Lock, Sparkles, Download, ChevronDown, Presentation as PresentationIcon, Crown } from 'lucide-react';
import { api } from '../api';
import { toast } from 'sonner';
import { exportGoldPDF } from '../utils/exportPDF';
import { exportGoldPPTX } from '../utils/exportPPTX'; 

const FEATURES = [
  {
    id: 'script',
    name: 'Guión de Video',
    description: 'Guión completo para grabar un video educativo sobre el tema.',
    icon: Film,
    iconColor: 'text-violet-400',
    iconBg: 'bg-violet-500/20',
    border: 'border-violet-500/40',
    activeBorder: 'border-violet-400/60',
    glowColor: 'shadow-violet-500/10',
    badgeColor: 'bg-violet-500/20 text-violet-300',
    tier: 'gold'
  },
  {
    id: 'preexam',
    name: 'Pre-Examen',
    description: 'Cuestionario de preparación con preguntas variadas para el examen.',
    icon: ClipboardList,
    iconColor: 'text-blue-400',
    iconBg: 'bg-blue-500/20',
    border: 'border-blue-500/40',
    activeBorder: 'border-blue-400/60',
    glowColor: 'shadow-blue-500/10',
    badgeColor: 'bg-blue-500/20 text-blue-300',
    tier: 'gold'
  },
  {
    id: 'summary',
    name: 'Resumen para Alumno',
    description: 'Resumen claro y didáctico adaptado a la edad del estudiante.',
    icon: FileText,
    iconColor: 'text-emerald-400',
    iconBg: 'bg-emerald-500/20',
    border: 'border-emerald-500/40',
    activeBorder: 'border-emerald-400/60',
    glowColor: 'shadow-emerald-500/10',
    badgeColor: 'bg-emerald-500/20 text-emerald-300',
    tier: 'gold'
  },
  {
    id: 'slides',
    name: 'Presentación Teórica',
    description: 'Genera 10 diapositivas listas para descargar en PowerPoint.',
    icon: PresentationIcon,
    iconColor: 'text-amber-400',
    iconBg: 'bg-amber-500/20',
    border: 'border-amber-500/40',
    activeBorder: 'border-amber-400/60',
    glowColor: 'shadow-amber-500/10',
    badgeColor: 'bg-amber-500/20 text-amber-300',
    tier: 'ultra' // NUEVO: Esta función está marcada como exclusiva Ultra
  }
];

// FIX: Recibimos onOpenUltraPromo desde el Dashboard
export default function GoldFeatures({ user, onShowPricing, onCreditsUpdate, generatedResult, onOpenUltraPromo }) {
  const isGold = ['gold', 'ultra', 'institution_gold', 'institution_pro', 'institution_ultra'].includes(user?.plan);
  const isUltra = ['ultra', 'institution_ultra'].includes(user?.plan); // NUEVO: Detector estricto de Ultra
  const isUnlimited = user?.credits_remaining === -1;

  const [activeFeature, setActiveFeature] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  
  const [examType, setExamType] = useState('multiple');

  const activeFeatureData = FEATURES.find(f => f.id === activeFeature);

  const handleFeatureClick = (featureId) => {
    // Si el usuario no es ni Gold, mostramos precios generales
    if (!isGold) { 
        onShowPricing(); 
        return; 
    }
    
    // Si es Gold pero la función es Ultra (Diapositivas), abrimos el modal de venta Ultra
    const clickedFeature = FEATURES.find(f => f.id === featureId);
    if (clickedFeature.tier === 'ultra' && !isUltra) {
        if (onOpenUltraPromo) onOpenUltraPromo();
        else onShowPricing(); // Fallback por las dudas
        return;
    }
    
    if (!generatedResult) {
      toast.error('Primero generá una dinámica para que el sistema sepa qué tema desarrollar.');
      return;
    }

    if (activeFeature === featureId) {
      setActiveFeature(null);
      setResult(null);
    } else {
      setActiveFeature(featureId);
      setResult(null);
    }
  };

  const handleGenerate = async () => {
    if (!generatedResult || !activeFeature) return;
    
    setIsLoading(true);
    setResult(null);
    try {
      const { data } = await api.post('/gold/generate', {
        topic: generatedResult.topic,
        age: generatedResult.age,
        feature_type: activeFeature,
        exam_type: examType, 
        context_text: generatedResult.context_text || null,
      });
      setResult(data.result);
      if (data.credits_remaining !== undefined) onCreditsUpdate(data.credits_remaining);
    } catch (e) {
      if (e.response?.status === 402) onShowPricing();
      else if (e.response?.status === 403) onShowPricing();
      else toast.error(e.response?.data?.detail || 'Error al generar');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportGold = async () => {
    if (!result || !activeFeature || !generatedResult) return;
    setIsExporting(true);
    try {
      if (activeFeature === 'slides') {
        await exportGoldPPTX(generatedResult.topic, generatedResult.age, result);
        toast.success('PowerPoint descargado exitosamente');
      } else {
        await exportGoldPDF(activeFeature, generatedResult.topic, generatedResult.age, result);
        toast.success('PDF descargado');
      }
    } catch {
      toast.error('Error al generar el documento');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="border-t border-slate-800 pt-8 pb-8">
      {/* Section header */}
      <div className="flex items-center gap-3 mb-5">
        <div>
          <p className="text-xs text-amber-400 uppercase tracking-[0.15em] font-semibold mb-0.5">Funciones Exclusivas</p>
          <h3 className="text-white font-bold text-lg">Herramientas Avanzadas</h3>
        </div>
        {!isGold && (
          <button
            onClick={onShowPricing}
            className="ml-auto flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300 border border-amber-500/30 hover:border-amber-400/50 bg-amber-500/5 px-3 py-1.5 rounded-full transition-all"
          >
            <Lock size={11} /> Desbloquear con Gold
          </button>
        )}
      </div>

      {/* Feature cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {FEATURES.map(feature => {
          const Icon = feature.icon;
          const isActive = activeFeature === feature.id;
          
          // Lógica visual del candadito
          const isLocked = (!isGold && feature.tier === 'gold') || (!isUltra && feature.tier === 'ultra');

          return (
            <button
              key={feature.id}
              data-testid={`gold-feature-${feature.id}`}
              onClick={() => handleFeatureClick(feature.id)}
              className={`relative text-left p-4 rounded-xl border transition-all duration-200 ${
                isActive
                  ? `${feature.activeBorder} bg-slate-900 shadow-lg ${feature.glowColor}`
                  : `${feature.border} bg-gradient-to-br from-slate-900 to-slate-900/70 hover:from-slate-800 hover:shadow-md hover:${feature.glowColor}`
              }`}
            >
              {/* Badges y Candados */}
              <div className="absolute top-2.5 right-2.5 flex items-center gap-1">
                {isLocked && <Lock size={10} className={feature.tier === 'ultra' ? 'text-blue-400' : 'text-amber-500/70'} />}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                    feature.tier === 'ultra' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                }`}>
                  {feature.tier}
                </span>
              </div>

              <div className={`w-8 h-8 rounded-lg ${feature.iconBg} flex items-center justify-center mb-2.5 border border-white/5`}>
                <Icon size={16} strokeWidth={1.5} className={feature.iconColor} />
              </div>
              <p className="text-slate-200 text-xs font-semibold mb-1 pr-12">{feature.name}</p>
              <p className="text-slate-500 text-[10px] leading-relaxed">{feature.description}</p>

              {/* Bottom accent line */}
              <div className={`absolute bottom-0 left-4 right-4 h-px ${
                isActive ? `bg-gradient-to-r from-transparent via-${feature.iconColor.replace('text-', '')} to-transparent opacity-60` : 'bg-transparent'
              }`} />
            </button>
          );
        })}
      </div>

      {/* Active feature form */}
      {isGold && activeFeature && generatedResult && (
        <div className={`mt-4 bg-slate-900 border ${activeFeatureData?.activeBorder} rounded-xl p-5 space-y-4 animate-fade-in-up`}>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
             <div className="flex items-start gap-3">
               <activeFeatureData.icon size={20} strokeWidth={1.5} className={`${activeFeatureData.iconColor} mt-1`} />
               <div>
                 <p className="text-slate-200 text-sm font-semibold">Generar {activeFeatureData.name}</p>
                 <p className="text-slate-400 text-xs mt-0.5">
                   Se procesará en base al tema: <strong className="text-slate-300">{generatedResult.topic}</strong> ({generatedResult.age})
                 </p>
               </div>
             </div>
             
             <div className="flex flex-col md:flex-row items-center gap-3 w-full md:w-auto">
               
               {activeFeature === 'preexam' && (
                  <div className="relative w-full md:w-48">
                    <select 
                      value={examType} 
                      onChange={e => setExamType(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2.5 text-xs font-semibold text-slate-300 outline-none focus:border-blue-500 transition-all appearance-none cursor-pointer"
                    >
                      <option value="multiple">Múltiple Choice (10)</option>
                      <option value="desarrollo">Desarrollo Reflexivo (5)</option>
                    </select>
                    <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                  </div>
               )}

               <div className="flex items-center gap-3 w-full md:w-auto justify-end">
                 {!isUnlimited && <span className="text-xs text-slate-500 font-medium whitespace-nowrap">−1 crédito</span>}
                 <button
                   data-testid="gold-generate-btn"
                   onClick={handleGenerate}
                   disabled={isLoading}
                   className="w-full md:w-auto px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
                 >
                   {isLoading
                     ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                     : <Sparkles size={14} strokeWidth={1.5} />
                   }
                   Generar Ahora
                 </button>
               </div>
             </div>
          </div>

          {/* Result display */}
          {result && (
            <div className="border-t border-slate-800 pt-4 mt-4">
              <div className="flex items-center justify-end mb-3">
                <button
                  data-testid="gold-export-pdf-btn"
                  onClick={handleExportGold}
                  disabled={isExporting}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-slate-200 border border-slate-700 text-xs transition-all disabled:opacity-50"
                >
                  <Download size={12} strokeWidth={1.5} />
                  {isExporting ? 'Generando...' : (activeFeature === 'slides' ? 'Descargar PowerPoint' : 'Exportar PDF')}
                </button>
              </div>
              {activeFeature === 'script' && <ScriptResult data={result} />}
              {activeFeature === 'preexam' && <PreExamResult data={result} />}
              {activeFeature === 'summary' && <SummaryResult data={result} />}
              {activeFeature === 'slides' && <SlidesResult data={result} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ScriptResult({ data }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-violet-400 font-bold text-sm">{data.titulo}</h4>
        <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">{data.duracion_estimada}</span>
      </div>
      {data.introduccion && (
        <div className="bg-slate-800 rounded-xl p-3">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Introducción</p>
          <p className="text-violet-300 text-xs italic mb-1">"{data.introduccion.gancho || data.introduccion.desafio_apertura}"</p>
          <p className="text-slate-300 text-xs leading-relaxed">{data.introduccion.contenido}</p>
        </div>
      )}
      {data.secciones?.map((s, i) => (
        <div key={i} className="bg-slate-800 rounded-xl p-3">
          <p className="text-slate-300 text-xs font-semibold mb-1">{s.titulo}</p>
          <p className="text-slate-400 text-xs leading-relaxed">{s.guion}</p>
          {s.recurso_visual && <p className="text-slate-600 text-xs mt-1 italic">Visual: {s.recurso_visual}</p>}
        </div>
      ))}
      {data.cierre && (
        <div className="bg-slate-800 rounded-xl p-3">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Cierre</p>
          <p className="text-slate-300 text-xs leading-relaxed">{data.cierre}</p>
        </div>
      )}
    </div>
  );
}

function PreExamResult({ data }) {
  const [reveal, setReveal] = useState(false);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-blue-400 font-bold text-sm">{data.titulo}</h4>
        <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">{data.tiempo_estimado}</span>
      </div>
      {data.instrucciones && <p className="text-slate-400 text-xs">{data.instrucciones}</p>}
      <div className="space-y-2">
        {data.preguntas?.map((q, i) => (
          <div key={i} className="bg-slate-800 rounded-xl p-3">
            <p className="text-slate-200 text-xs font-medium mb-2">{q.numero}. {q.pregunta}</p>
            
            {q.opciones && q.opciones.length > 0 && (
              <div className="space-y-0.5">
                {q.opciones.map((opt, j) => (
                  <div key={j} className={`text-xs px-2 py-1 rounded ${reveal && opt.startsWith(q.respuesta_correcta) ? 'text-emerald-400 bg-emerald-500/10' : 'text-slate-400'}`}>
                    {opt}
                  </div>
                ))}
              </div>
            )}

            {(!q.opciones || q.opciones.length === 0) && reveal && (
               <div className="mt-3 space-y-2 border-t border-slate-700 pt-2">
                 <div>
                   <p className="text-[10px] text-emerald-500 font-bold uppercase tracking-wider">Respuesta Esperada:</p>
                   <p className="text-emerald-400/90 text-xs leading-relaxed">{q.respuesta_correcta}</p>
                 </div>
                 {q.explicacion && (
                   <div>
                     <p className="text-[10px] text-blue-400 font-bold uppercase tracking-wider">Criterios de Evaluación:</p>
                     <p className="text-blue-400/90 text-xs leading-relaxed">{q.explicacion}</p>
                   </div>
                 )}
               </div>
            )}
            
            {q.opciones && q.opciones.length > 0 && reveal && q.explicacion && (
              <p className="text-slate-500 text-xs mt-2 italic border-t border-slate-700 pt-2">→ {q.explicacion}</p>
            )}
          </div>
        ))}
      </div>
      <button
        onClick={() => setReveal(!reveal)}
        className="text-xs text-slate-500 hover:text-blue-400 border border-slate-700 hover:border-blue-500/40 rounded-lg px-3 py-1.5 w-full transition-all"
      >
        {reveal ? 'Ocultar respuestas/criterios' : 'Revelar respuestas/criterios'}
      </button>
    </div>
  );
}

function SummaryResult({ data }) {
  return (
    <div className="space-y-3">
      <h4 className="text-emerald-400 font-bold text-sm">{data.titulo}</h4>
      {data.objetivos && (
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1.5">Objetivos</p>
          <ul className="space-y-1">{data.objetivos?.map((o, i) => (
            <li key={i} className="text-slate-300 text-xs flex gap-2"><span className="text-emerald-400 flex-shrink-0">•</span>{o}</li>
          ))}</ul>
        </div>
      )}
      {data.conceptos_clave && (
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1.5">Conceptos clave</p>
          <div className="space-y-1.5">{data.conceptos_clave?.map((c, i) => (
            <div key={i} className="bg-slate-800 rounded-lg px-3 py-2">
              <span className="text-emerald-300 text-xs font-semibold">{c.termino}: </span>
              <span className="text-slate-400 text-xs">{c.definicion}</span>
            </div>
          ))}</div>
        </div>
      )}
      {data.resumen_narrativo && (
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1.5">Resumen</p>
          <p className="text-slate-300 text-xs leading-relaxed">{data.resumen_narrativo}</p>
        </div>
      )}
      {data.para_recordar && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Para recordar</p>
          <p className="text-emerald-300 text-sm font-medium italic">"{data.para_recordar}"</p>
        </div>
      )}
    </div>
  );
}

function SlidesResult({ data }) {
  return (
    <div className="space-y-3">
      <h4 className="text-amber-400 font-bold text-sm">{data.titulo_presentacion}</h4>
      <p className="text-xs text-slate-500 mb-3">Revisá el contenido de tus diapositivas antes de descargar el PowerPoint.</p>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {data.diapositivas?.map((slide, i) => (
          <div key={i} className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <p className="text-[10px] text-amber-500/70 font-bold uppercase tracking-wider mb-1">Diapositiva {slide.numero}</p>
            <p className="text-slate-200 text-xs font-bold mb-2">{slide.titulo}</p>
            <ul className="space-y-1">
              {slide.vinetas?.map((v, j) => (
                <li key={j} className="text-slate-400 text-[11px] flex gap-1.5 leading-tight">
                  <span className="text-amber-500/50 flex-shrink-0">•</span> {v}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
